import {lazy, Suspense, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import {Routes, Route, Navigate, useLocation} from 'react-router-dom';
import {LoadingOverlay, MantineProvider} from '@mantine/core';
import {NotificationsProvider} from '@mantine/notifications';

// Global components
import OrderListener from 'components/others/OrderListener';

// Global configs
import mantineTheme from 'config/mantineTheme';
import mantineStyles from 'config/mantineStyles';
import mantineDefaultProps from 'config/mantineDefaultProps';
import routes from 'config/routes';

// Global utils
import crashLogger from 'utils/logger';
import {setupDayjs} from 'utils/i18n';
import {initializeElectronData} from 'utils/electron';
import analytics from 'utils/analytics';
import {socket} from 'utils/socket';

// Redux
import {setConnectedToUser, setElectronData} from 'store/actions';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {isEmpty} from 'lodash';
import {useBarcodeScanner} from 'hooks/useScanner';
import {qrActionPrefix} from 'utils/constants';
import {orderPickedUp, updateKdsOrder} from 'request/pos';

setupDayjs();
const queryClient = new QueryClient();

const importRetry = async (importFn, retries = 2, interval = 1000) => {
	try {
		return await importFn();
	} catch (error) {
		if (retries) {
			await new Promise(resolve => setTimeout(resolve, interval));
			return importRetry(importFn, retries - 1, interval);
		} else {
			window.location.replace('/');
			crashLogger.error(error);
		}
	}
};

const renderRoutes = routeList => {
	return routeList.map((route, i) => {
		// eslint-disable-next-line react/jsx-max-props-per-line
		let element = route.redirect ? <Navigate to="/" replace /> : route.element;
		if (typeof route.element === 'string') {
			const Lazy = lazy(() => importRetry(() => import(route.element + '/index.jsx')));
			element = (
				<Suspense fallback={(
					<LoadingOverlay visible />
				)}>
					<Lazy />
				</Suspense>
			);
		}
		return (
			<Route
				key={i}
				path={route.path || '*'}
				element={element}
			>
				{route.children && renderRoutes(route.children)}
			</Route>
		);
	});
};

function App() {
	const dispatch = useDispatch();
	const location = useLocation();

	const callHandler = async (callFn, payload, postCallFn) => {
		try {
			if (window.handlingCall) return;
			window.handlingCall = true;
			const response = await callFn(payload);
			if (response.success) {
				postCallFn?.(response.identifier);
			}
			window.handlingCall = false;
		} catch (error) {
			console.log(error);
			window.handlingCall = false;
		}
	};

	useBarcodeScanner((code = '') => {
		if (window.handlingCall) return;
		if (!code.startsWith(qrActionPrefix.BASE)) {
			console.warn('Invalid QR format');
			return;
		}

		let payload = {};
		let callFn = () => null;
		let postCallFn = () => null;

		const codePrefix = code.slice(0, qrActionPrefix.BASE.length + 2);

		switch (codePrefix) {
			case qrActionPrefix.LABEL_QR:
				payload.orderId = Number(code.slice(qrActionPrefix.LABEL_QR.length));
				payload.station = 'checker';
				payload.odsDoneFrom = 'scanner';
				callFn = updateKdsOrder;
				break;
			case qrActionPrefix.DRIVER_QR:
				payload.orderId = Number(code.slice(qrActionPrefix.DRIVER_QR.length));
				callFn = orderPickedUp;
				break;
			default:
				console.warn('Unknown QR type');
		}

		if (payload.orderId) payload.orderId = Number(payload.orderId);

		if (!isEmpty(payload)) callHandler(callFn, payload, postCallFn);
	});

	useEffect(() => {
		initializeElectronData();
		analytics.initialize();
		socket.on('socket_status', ({connected}) => {
			dispatch(setConnectedToUser({connected}));
		});
		socket.on('fresto_settings', ({autoPrintDisabled}) => {
			dispatch(setElectronData({autoPrintDisabled}));
		});
		return () => {
			socket.off('socket_status');
			socket.off('fresto_settings');
		};
	}, []);
	useEffect(() => {
		analytics.pageview();
	}, [location.pathname]);

	return (
		<MantineProvider
			theme={mantineTheme}
			styles={mantineStyles}
			defaultProps={mantineDefaultProps}
		>
			<QueryClientProvider client={queryClient}>
				<NotificationsProvider
					position="top-right"
					className="notification-provider">
					<OrderListener />
					<Suspense fallback={null}>
						<Routes>
							{renderRoutes(routes)}
						</Routes>
					</Suspense>
				</NotificationsProvider>
			</QueryClientProvider>
		</MantineProvider>
	);
}

export default App;
